import {
  IonCol,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonRow,
  IonText,
} from '@ionic/react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { checkmarkDone, download } from 'ionicons/icons';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  ConversationContext,
  TranslationContext,
  UserContext,
} from '../../contexts';
import { AuthorType, Message, MessageType } from '../../models';

interface Props {
  isLast: boolean;
  item: Message;
  previousItem?: Message;
}

export const ChatItem = ({ isLast, item, previousItem }: Props) => {
  const { user, isExpert } = useContext(UserContext);
  const { setIsExpertTyping } = useContext(ConversationContext);
  const { translate } = useContext(TranslationContext);
  const { author, authorType, message, type, date, talismanUrl } = item;

  const talismanSrc = useMemo(() => {
    if (!talismanUrl) {
      return undefined;
    }
    // talismanUrl is a path relative to the API host (e.g. "/talismans/xxx.jpg").
    return `${process.env.REACT_APP_API_ENTRYPOINT ?? ''}${talismanUrl}`;
  }, [talismanUrl]);

  const downloadTalisman = useCallback(async () => {
    if (!talismanSrc) {
      return;
    }
    // Fetch as a blob so the download is forced even when the file is served
    // from a different origin (the `download` attribute is ignored cross-origin).
    const response = await fetch(talismanSrc);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = talismanSrc.split('/').pop() ?? 'talisman.jpg';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }, [talismanSrc]);

  const isMe = useMemo(() => {
    // If we have authorType, use it to determine if message is from current user
    if (authorType && user) {
      // Customer viewing their own messages
      if (authorType === AuthorType.CUSTOMER && !isExpert) {
        return true;
      }
      // Expert viewing their own messages
      if (authorType === AuthorType.EXPERT && isExpert) {
        return true;
      }
      // AI, System, or Chatscript messages are never "me"
      return false;
    }
    // Fallback to name comparison (for legacy messages without authorType)
    return user && author === user.name;
  }, [author, user, authorType, isExpert]);
  const isInfo = useMemo(() => type === MessageType.INFO, [type]);
  const isChatscriptInfo = useMemo(
    () =>
      [
        MessageType.CHATSCRIPT_ACTIVATE,
        MessageType.CHATSCRIPT_DEACTIVATE,
      ].includes(type),
    [type],
  );
  const sentByChatscript = useMemo(() => {
    return authorType === AuthorType.CHATSCRIPT;
  }, [authorType]);
  const sentByAI = useMemo(() => {
    return authorType === AuthorType.AI;
  }, [authorType]);
  const isJoin = useMemo(() => type === MessageType.JOIN, [type]);
  const isCreditAdd = useMemo(() => type === MessageType.CREDIT_ADDED, [type]);
  const isLeave = useMemo(() => type === MessageType.LEAVE, [type]);
  const isStart = useMemo(() => type === MessageType.START, [type]);
  const isWarning = useMemo(
    () =>
      [MessageType.ONE_MINUTE_LEFT, MessageType.CREDIT_PENDING].includes(type),
    [type],
  );
  const isAlert = useMemo(() => type === MessageType.STOP, [type]);
  const isMessage = useMemo(
    () =>
      type === MessageType.MESSAGE ||
      type === MessageType.CUSTOMER_HAS_BEEN_INACTIVE,
    [type],
  );
  const isResumed = useMemo(() => type === MessageType.RESUME, [type]);
  const isExpertStartTyping = useMemo(() => {
    return (
      type === MessageType.EXPERT_START_TYPING &&
      date &&
      new Date(date).getTime() > Date.now() - 5000
    );
  }, [type, date]);
  const isOldExpertStartTyping = useMemo(() => {
    return (
      type === MessageType.EXPERT_START_TYPING &&
      date &&
      new Date(date).getTime() < Date.now() - 5000
    );
  }, [type, date]);
  const isExpertFinishTyping = useMemo(
    () => type === MessageType.EXPERT_FINISH_TYPING,
    [type],
  );
  const isExpertEnteredChat = useMemo(
    () => type === MessageType.EXPERT_ENTERED_CHAT,
    [type],
  );
  // The backend emits both 'conversation-start' and 'conversation-expert-entered-chat' at the
  // start of a conversation, and both render the same "Conversation started" banner. Treat them as
  // one logical event and only show the first of a consecutive run, so the history shows a single
  // "Conversation started".
  const isConversationStarted = useMemo(
    () => isStart || isExpertEnteredChat,
    [isStart, isExpertEnteredChat],
  );
  const previousIsConversationStarted = useMemo(
    () =>
      previousItem?.type === MessageType.START ||
      previousItem?.type === MessageType.EXPERT_ENTERED_CHAT,
    [previousItem],
  );
  const isPong = useMemo(() => type === MessageType.PONG, [type]);
  const isPing = useMemo(() => type === MessageType.PING, [type]);

  const isVisible = useMemo(
    () =>
      !(isConversationStarted && previousIsConversationStarted) &&
      (isExpert ||
        (!isJoin &&
          !isLeave &&
          !isCreditAdd &&
          !isResumed &&
          !isChatscriptInfo &&
          !isExpertStartTyping &&
          !isExpertFinishTyping &&
          !isPong &&
          !isPing &&
          !isOldExpertStartTyping)),
    [
      isConversationStarted,
      previousIsConversationStarted,
      isCreditAdd,
      isExpert,
      isJoin,
      isLeave,
      isResumed,
      isChatscriptInfo,
      isExpertStartTyping,
      isExpertFinishTyping,
      isPong,
      isPing,
      isOldExpertStartTyping,
    ],
  );
  dayjs.extend(relativeTime);

  useEffect(() => {
    if (isExpertStartTyping && setIsExpertTyping) {
      setIsExpertTyping(true);
    }
    if (isExpertFinishTyping && setIsExpertTyping) {
      setIsExpertTyping(false);
    }
  }, [isExpertStartTyping, isExpertFinishTyping, setIsExpertTyping]);

  if (!isVisible) {
    return null;
  }

  return (
    <IonItemSliding>
      <IonItem
        className={classnames('chat-item', {
          'chat-item--alert': isAlert,
          'chat-item--info': isInfo || isJoin || isPing,
          'chat-item--message': isMessage,
          'chat-item--right':
            isMessage &&
            !isMe &&
            (!isExpert || (!sentByAI && !sentByChatscript)),
          'chat-item--start': isStart || isExpertEnteredChat,
          'chat-item--warning': isWarning,
        })}
      >
        <IonRow className="chat-item-row">
          <IonCol size="12" className="chat-item-col">
            <div className="chat-item--container">
              <IonLabel className="chat-item-label">
                <div className="chat-item--container--text">
                  <p>
                    {sentByChatscript && isExpert ? '🤖 ' : ''}
                    {sentByAI && isExpert ? '🤖🧠 ' : ''}
                    {isPing ? translate('chat.networkIssue') : message}
                  </p>
                  {talismanSrc ? (
                    <>
                      <img
                        className="chat-item--talisman"
                        src={talismanSrc}
                        alt={translate('chat.talismanAlt')}
                      />
                      <button
                        type="button"
                        className="chat-item--talisman-download"
                        onClick={downloadTalisman}
                      >
                        <IonIcon icon={download} />
                        {translate('chat.talismanDownload')}
                      </button>
                    </>
                  ) : null}
                </div>
              </IonLabel>
            </div>
          </IonCol>
        </IonRow>
      </IonItem>
      {isMessage && isLast ? (
        <IonRow>
          <IonCol size="12">
            <div
              className={classnames('timer-item', {
                'timer-item--right': !isMe,
              })}
            >
              <p className="timer-item--text">
                {dayjs(new Date(date)).fromNow()}
              </p>
              <IonIcon icon={checkmarkDone} className="first-tick" />
            </div>
          </IonCol>
        </IonRow>
      ) : (
        <IonRow>
          <IonCol size="12" />
        </IonRow>
      )}
      <IonItemOptions className="chat-item-ion-options">
        <IonItemOption>
          <IonText className="chat-item-time">
            {new Date(date).toLocaleTimeString(navigator.language, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </IonText>
        </IonItemOption>
      </IonItemOptions>
    </IonItemSliding>
  );
};
